import ExcelJS from 'exceljs';
import {
  formatWebDocumentAcademicYearLabel,
  isWebDocumentSchoolYearMetaRow,
  type EvaluationPlanWebDocumentData,
  getExampleAnswerTitle,
  getWebDocumentDualLabelLines,
  isWebDocumentGubunColumn,
  webDocumentColumnDisplayHeader,
} from '../types/evaluationPlanWebDocument';

function solidCellBorder(): Partial<ExcelJS.Borders> {
  const side: ExcelJS.Border = { style: 'thin', color: { argb: 'FF000000' } };
  return {
    top: { ...side },
    left: { ...side },
    bottom: { ...side },
    right: { ...side },
  };
}

const HEADER_FILL: ExcelJS.Fill = {
  type: 'pattern',
  pattern: 'solid',
  fgColor: { argb: 'FFECECEC' },
};

function displayWidthOfText(text: string): number {
  let width = 0;
  for (const ch of text) {
    width += ch.charCodeAt(0) > 255 ? 2 : 1;
  }
  return width;
}

function triggerBrowserDownload(buffer: ArrayBuffer, fileName: string): void {
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName.endsWith('.xlsx') ? fileName : `${fileName}.xlsx`;
  anchor.click();
  URL.revokeObjectURL(url);
}

function applyLabelCellStyle(cell: ExcelJS.Cell): void {
  cell.border = solidCellBorder();
  cell.font = { bold: true };
  cell.fill = HEADER_FILL;
  cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
}

function applyValueCellStyle(cell: ExcelJS.Cell, centered = false): void {
  cell.border = solidCellBorder();
  cell.alignment = {
    horizontal: centered ? 'center' : 'left',
    vertical: 'top',
    wrapText: true,
  };
}

function getExampleAnswerContent(data: EvaluationPlanWebDocumentData): string {
  const current = data.exampleAnswer ?? { example: '', answer: '' };
  if (current.example && current.answer) {
    return `${current.example}\n${current.answer}`;
  }
  return current.example || current.answer || '';
}

function buildColumnWidths(columnCount: number, rows: string[][]): number[] {
  const baseWidths = Array.from({ length: columnCount }, (_, index) => {
    if (index === 0) return 14;
    return Math.floor(86 / Math.max(1, columnCount - 1));
  });
  return baseWidths.map((base, colIndex) => {
    const contentWch = Math.max(
      ...rows.map((row) => displayWidthOfText((row[colIndex] ?? '').trim())),
      2
    );
    return Math.min(72, Math.max(base, contentWch + 1.5));
  });
}

export async function saveEvaluationPlanWebDocumentAsExcel(params: {
  data: EvaluationPlanWebDocumentData;
  fileName: string;
  sheetName?: string;
}): Promise<void> {
  const { data, fileName, sheetName = '평가기준안' } = params;
  const columnCount = Math.max(4, data.columns.length);
  const visibleMetaRows = data.metaRows.filter((row) => !isWebDocumentSchoolYearMetaRow(row) && !row.hidden);
  const exampleAnswerContent = getExampleAnswerContent(data);

  const workbook = new ExcelJS.Workbook();
  const sheet = workbook.addWorksheet(sheetName);

  const allTextRows: string[][] = [];
  let rowIndex = 1;

  sheet.mergeCells(rowIndex, 1, rowIndex, columnCount);
  const titleCell = sheet.getCell(rowIndex, 1);
  titleCell.value = data.title.trim() || '평가 기준안';
  titleCell.font = { bold: true, size: 14 };
  titleCell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
  allTextRows.push([titleCell.value as string]);
  rowIndex += 1;

  sheet.mergeCells(rowIndex, 1, rowIndex, columnCount);
  const yearCell = sheet.getCell(rowIndex, 1);
  yearCell.value = formatWebDocumentAcademicYearLabel();
  yearCell.font = { bold: true, size: 11 };
  yearCell.alignment = { horizontal: 'left', vertical: 'middle' };
  allTextRows.push([yearCell.value as string]);
  rowIndex += 1;

  rowIndex += 1;

  visibleMetaRows.forEach((metaRow) => {
    if (metaRow.layout === 'pair') {
      const row = sheet.getRow(rowIndex);
      const cells = [metaRow.label, metaRow.value, metaRow.pairLabel ?? '', metaRow.pairValue ?? ''];
      cells.forEach((value, colOffset) => {
        const cell = row.getCell(colOffset + 1);
        cell.value = value;
        if (colOffset % 2 === 0) applyLabelCellStyle(cell);
        else applyValueCellStyle(cell);
      });
      allTextRows.push(cells);
      rowIndex += 1;
      return;
    }

    if (metaRow.layout === 'dual_label') {
      const lines = getWebDocumentDualLabelLines(metaRow);
      const row = sheet.getRow(rowIndex);
      const labelCell = row.getCell(1);
      labelCell.value = lines.join('\n');
      applyLabelCellStyle(labelCell);
      sheet.mergeCells(rowIndex, 2, rowIndex, columnCount);
      const valueCell = row.getCell(2);
      valueCell.value = metaRow.value;
      applyValueCellStyle(valueCell);
      allTextRows.push([labelCell.value as string, valueCell.value as string]);
      rowIndex += 1;
      return;
    }

    if (!metaRow.label && !metaRow.isUserAdded) return;

    const row = sheet.getRow(rowIndex);
    const labelCell = row.getCell(1);
    labelCell.value = metaRow.label;
    applyLabelCellStyle(labelCell);
    sheet.mergeCells(rowIndex, 2, rowIndex, columnCount);
    const valueCell = row.getCell(2);
    valueCell.value = metaRow.value;
    applyValueCellStyle(valueCell);
    allTextRows.push([labelCell.value as string, valueCell.value as string]);
    rowIndex += 1;
  });

  const exampleHeaderRow = sheet.getRow(rowIndex);
  sheet.mergeCells(rowIndex, 1, rowIndex, columnCount);
  const exampleHeaderCell = exampleHeaderRow.getCell(1);
  const exampleTitle = getExampleAnswerTitle(data);
  exampleHeaderCell.value = exampleTitle;
  applyLabelCellStyle(exampleHeaderCell);
  allTextRows.push([exampleTitle]);
  rowIndex += 1;

  const exampleBodyRow = sheet.getRow(rowIndex);
  sheet.mergeCells(rowIndex, 1, rowIndex, columnCount);
  const exampleBodyCell = exampleBodyRow.getCell(1);
  exampleBodyCell.value = exampleAnswerContent;
  applyValueCellStyle(exampleBodyCell);
  allTextRows.push([exampleAnswerContent]);
  rowIndex += 1;

  rowIndex += 1;

  const rubricHeaderRow = sheet.getRow(rowIndex);
  const rubricHeaders = data.columns.map((column) => webDocumentColumnDisplayHeader(column));
  while (rubricHeaders.length < columnCount) rubricHeaders.push('');
  rubricHeaders.slice(0, columnCount).forEach((header, colOffset) => {
    const cell = rubricHeaderRow.getCell(colOffset + 1);
    cell.value = header;
    applyLabelCellStyle(cell);
  });
  /** 두 줄 제목(예: '채점\n기준')이면 행 높이 확보 */
  if (rubricHeaders.some((h) => h.includes('\n'))) rubricHeaderRow.height = 32;
  allTextRows.push(rubricHeaders.slice(0, columnCount));
  rowIndex += 1;

  data.rows.forEach((rubricRow) => {
    const row = sheet.getRow(rowIndex);
    const cells = [rubricRow.label];
    if (rubricRow.memo?.trim()) {
      cells[0] = `${rubricRow.label}\n${rubricRow.memo.trim()}`;
    }
    data.columns
      .filter((col) => !isWebDocumentGubunColumn(col))
      .forEach((column) => {
        cells.push(rubricRow.cells[column.id] ?? '');
      });
    while (cells.length < columnCount) cells.push('');
    cells.slice(0, columnCount).forEach((value, colOffset) => {
      const cell = row.getCell(colOffset + 1);
      cell.value = value;
      if (colOffset === 0) applyLabelCellStyle(cell);
      else applyValueCellStyle(cell);
    });
    allTextRows.push(cells.slice(0, columnCount));
    rowIndex += 1;
  });

  const columnWidths = buildColumnWidths(columnCount, allTextRows);
  sheet.columns = columnWidths.map((width) => ({ width }));

  const buffer = await workbook.xlsx.writeBuffer();
  triggerBrowserDownload(buffer, fileName);
}
